import { gsap } from 'gsap';

// Add your custom JS here.

/**
 * Navbar scroll behavior
 * Adds 'scrolled' class to navbar when user scrolls past hero section
 */
document.addEventListener('DOMContentLoaded', function () {
	const navbar = document.querySelector('#main-nav');
	const rebrandingBanner = document.querySelector('.rebranding-banner');

	if (navbar) {
		// Calculate the scroll threshold (banner height + some offset)
		const scrollThreshold = rebrandingBanner ? rebrandingBanner.offsetHeight + 100 : 100;

		function handleScroll() {
			if (window.scrollY > scrollThreshold) {
				navbar.classList.add('scrolled');
			} else {
				navbar.classList.remove('scrolled');
			}
		}

		// Initial check
		handleScroll();

		// Add scroll listener with throttling for performance
		let ticking = false;
		window.addEventListener('scroll', function () {
			if (!ticking) {
				window.requestAnimationFrame(function () {
					handleScroll();
					ticking = false;
				});
				ticking = true;
			}
		});
	}

	// Service Categories - See More functionality
	const seeMoreButtons = document.querySelectorAll('[data-see-more-btn]');

	seeMoreButtons.forEach(function (button) {
		button.addEventListener('click', function () {
			// Find the parent section and get the grid
			const wrapper = this.closest('.service-categories-wrapper');
			if (!wrapper) return;

			const grid = wrapper.querySelector('[data-service-categories-grid]');
			if (!grid) return;

			// Get all hidden items (handle both previous and editorial layouts)
			const hiddenItems = grid.querySelectorAll('.category-card-hidden, .sc-item-hidden');
			const isExpanded = this.classList.contains('is-expanded');
			const textSpan = this.querySelector('.see-more-text');

			if (isExpanded) {
				// Collapse - hide items again
				hiddenItems.forEach(function (item) {
					item.classList.remove('is-visible');
				});
				this.classList.remove('is-expanded');

				// Revert text based on layout type
				if (this.classList.contains('sc-editorial-btn')) {
					textSpan.textContent = 'Display All Offerings';
				} else {
					textSpan.textContent = 'See More Services';
				}

				// Scroll back to the grid
				wrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });
			} else {
				// Expand - show hidden items
				hiddenItems.forEach(function (item) {
					item.classList.add('is-visible');
				});
				this.classList.add('is-expanded');
				textSpan.textContent = 'Show Less';
			}
		});
	});

	// Service Categories - Version 2 expand/collapse functionality
	const scExpandButtons = document.querySelectorAll('[data-sc-expand]');

	scExpandButtons.forEach(function (button) {
		button.addEventListener('click', function () {
			// Find the parent article and toggle expanded class
			const article = this.closest('.sc-item-editorial');
			if (!article) return;

			const isExpanded = article.classList.contains('is-expanded');

			if (isExpanded) {
				// Collapse
				article.classList.remove('is-expanded');
				this.classList.remove('is-expanded');
				this.querySelector('.sc-read-more-text').textContent = 'Read more';
			} else {
				// Expand
				article.classList.add('is-expanded');
				this.classList.add('is-expanded');
				this.querySelector('.sc-read-more-text').textContent = 'Read less';
			}
		});
	});

	// ===========================================
	// Project Gallery Filtering
	// ===========================================
	initProjectsFilter();

	// ===========================================
	// Project Gallery Lightbox Navigation
	// ===========================================
	initProjectGalleryLightbox();
});

/**
 * Initialize category filtering for project galleries
 */
function initProjectsFilter() {
	const filters = document.querySelectorAll('.projects-filter');
	if (!filters.length) return;

	filters.forEach(function (filterContainer) {
		const buttons = filterContainer.querySelectorAll('.filter-btn');
		// The grid is usually a sibling or within the same parent section
		const section = filterContainer.closest('.projects-gallery-block');
		if (!section) return;

		const grid = section.querySelector('.projects-grid');
		if (!grid) return;

		const cards = grid.querySelectorAll('.project-card');

		buttons.forEach(function (btn) {
			btn.addEventListener('click', function () {
				const filterValue = this.getAttribute('data-filter');

				// Update active state for buttons
				buttons.forEach(b => b.classList.remove('active'));
				this.classList.add('active');

				// Separate cards into those to show and those to hide
				const toShow = [];
				const toHide = [];

				cards.forEach(function (card) {
					const categories = card.getAttribute('data-categories') || '';
					const categoryArray = categories.split(' ');
					const shouldShow = filterValue === 'all' || categoryArray.includes(filterValue);

					if (shouldShow) {
						toShow.push(card);
					} else {
						toHide.push(card);
					}
				});

				// 1. Fade out cards to hide
				if (toHide.length > 0) {
					gsap.to(toHide, {
						opacity: 0,
						scale: 0.9,
						y: 20,
						duration: 0.3,
						stagger: 0.05,
						ease: 'power2.in',
						onComplete: () => {
							toHide.forEach(card => card.classList.add('is-hidden'));
						}
					});
				}

				// 2. Fade in cards to show
				if (toShow.length > 0) {
					// Prepare cards for entrance
					toShow.forEach(card => {
						card.classList.remove('is-hidden');
						// Reset properties for GSAP animation
						gsap.set(card, { opacity: 0, scale: 0.9, y: 20 });
					});

					// Animate entrance with slight delay to allow hide to progress
					gsap.to(toShow, {
						opacity: 1,
						scale: 1,
						y: 0,
						duration: 0.5,
						stagger: 0.08,
						ease: 'power3.out',
						delay: toHide.length > 0 ? 0.35 : 0, // Wait for hide to finish
						clearProps: 'all' // Clear GSAP styles after animation
					});
				}

				// Optional: If using a layout library like Isotope or Masonry, 
				// you would trigger a relayout here. 
				// Since we're using CSS Grid with position: absolute for hidden items,
				// the remaining items will fill the gaps naturally if we didn't use absolute.
				// However, to keep the grid looking good, we might need to handle the layout.
				// For now, let's keep it simple as requested.
			});
		});
	});
}

/**
 * Initialize lightbox navigation for project galleries
 * Adds counter indicator and keyboard navigation support
 */
function initProjectGalleryLightbox() {
	// Find all project galleries
	const galleries = document.querySelectorAll('.project-gallery');

	if (!galleries.length) return;

	galleries.forEach(function (gallery) {
		const links = gallery.querySelectorAll('.gallery-item a[data-lightbox]');
		if (!links.length) return;

		const totalImages = links.length;
		let currentIndex = 0;

		// Create and append the counter element
		const counter = document.createElement('div');
		counter.className = 'gallery-lightbox-counter';
		counter.innerHTML = '<span class="current">1</span> / <span class="total">' + totalImages + '</span>';
		gallery.appendChild(counter);

		// Create navigation arrows
		const navContainer = document.createElement('div');
		navContainer.className = 'gallery-lightbox-nav';
		navContainer.innerHTML = `
			<button class="gallery-nav-btn gallery-nav-prev" aria-label="Previous image">
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<polyline points="15 18 9 12 15 6"></polyline>
				</svg>
			</button>
			<button class="gallery-nav-btn gallery-nav-next" aria-label="Next image">
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
					<polyline points="9 18 15 12 9 6"></polyline>
				</svg>
			</button>
		`;
		gallery.appendChild(navContainer);

		const prevBtn = navContainer.querySelector('.gallery-nav-prev');
		const nextBtn = navContainer.querySelector('.gallery-nav-next');

		// Function to show/hide controls based on lightbox state
		function updateControlsVisibility() {
			// Check for common lightbox indicators
			const lightboxSelectors = [
				'.mfp-wrap',                    // Magnific Popup
				'#lightbox',                     // Lightbox2
				'.lightbox',                     // Generic lightbox
				'.fancybox-wrap',                // Fancybox
				'.fancybox-stage',               // Fancybox stage
				'.pswp--open',                  // PhotoSwipe
				'.pswp',                        // PhotoSwipe container
				'.swipebox-inline-open',         // Swipebox
				'.pp_wrapper',                   // PrettyPhoto
				'.simple-lightbox'              // Simple Lightbox
			];

			const lightboxOpen = document.querySelector(lightboxSelectors.join(', '));

			if (lightboxOpen) {
				// Show controls when lightbox is open
				counter.classList.add('is-visible');
				navContainer.classList.add('is-visible');
				document.body.classList.add('gallery-lightbox-active');
			} else {
				// Hide controls when lightbox is closed
				counter.classList.remove('is-visible');
				navContainer.classList.remove('is-visible');
				document.body.classList.remove('gallery-lightbox-active');
			}
		}

		// Set up a periodic check for lightbox state (works with most plugins)
		let checkInterval = setInterval(updateControlsVisibility, 200);

		// Also listen for specific events that lightboxes might trigger
		const events = ['focusin', 'click', 'keydown'];
		events.forEach(function(eventType) {
			document.addEventListener(eventType, function() {
				// Small delay to allow lightbox to open
				setTimeout(updateControlsVisibility, 50);
			});
		});

		// Track current image index when lightbox opens
		links.forEach(function (link, index) {
			link.addEventListener('click', function () {
				currentIndex = index;
				updateCounter();
				// Check lightbox state after click
				setTimeout(updateControlsVisibility, 100);
			});
		});

		// Navigation functions
		function updateCounter() {
			counter.querySelector('.current').textContent = currentIndex + 1;
		}

		function navigateTo(index) {
			if (index < 0) {
				index = totalImages - 1;
			} else if (index >= totalImages) {
				index = 0;
			}
			currentIndex = index;
			updateCounter();
			// Trigger click on the link to open lightbox
			if (links[currentIndex]) {
				links[currentIndex].click();
			}
		}

		// Button click handlers
		prevBtn.addEventListener('click', function (e) {
			e.preventDefault();
			e.stopPropagation();
			navigateTo(currentIndex - 1);
		});

		nextBtn.addEventListener('click', function (e) {
			e.preventDefault();
			e.stopPropagation();
			navigateTo(currentIndex + 1);
		});

		// Keyboard navigation
		document.addEventListener('keydown', function (e) {
			// Only handle if lightbox is open
			const lightboxSelectors = [
				'.mfp-wrap', '#lightbox', '.lightbox', 
				'.fancybox-wrap', '.pswp--open', '.swipebox-inline-open'
			];
			const lightboxOpen = document.querySelector(lightboxSelectors.join(', '));
			
			if (!lightboxOpen) return;

			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				navigateTo(currentIndex - 1);
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				navigateTo(currentIndex + 1);
			} else if (e.key === 'Escape') {
				// Close lightbox - try common methods
				const closeBtn = document.querySelector('.mfp-close, #lightboxClose, .lightboxClose, .fancybox-close, .pswp__button--close');
				if (closeBtn) {
					closeBtn.click();
				}
			}
		});
	});

	// Add counter styles dynamically
	addLightboxNavStyles();
}

/**
 * Add styles for lightbox navigation elements
 */
function addLightboxNavStyles() {
	const style = document.createElement('style');
	style.textContent = `
		/* Gallery Lightbox Counter - hidden by default */
		.gallery-lightbox-counter {
			position: absolute;
			top: 1rem;
			right: 1rem;
			z-index: 1000;
			background: rgba(0, 0, 0, 0.75);
			color: #fff;
			padding: 0.5rem 1rem;
			border-radius: 50px;
			font-family: "Quicksand", sans-serif;
			font-size: 0.875rem;
			font-weight: 600;
			pointer-events: none;
			opacity: 0;
			visibility: hidden;
			transition: opacity 0.3s ease, visibility 0.3s ease;
		}

		/* Show counter when lightbox is open */
		.gallery-lightbox-counter.is-visible {
			opacity: 1;
			visibility: visible;
		}

		/* Gallery Navigation Arrows - hidden by default */
		.gallery-lightbox-nav {
			position: absolute;
			top: 50%;
			left: 0;
			right: 0;
			transform: translateY(-50%);
			display: flex;
			justify-content: space-between;
			padding: 0 0.5rem;
			pointer-events: none;
			z-index: 1000;
			opacity: 0;
			visibility: hidden;
			transition: opacity 0.3s ease, visibility 0.3s ease;
		}

		/* Show nav when lightbox is open */
		.gallery-lightbox-nav.is-visible {
			opacity: 1;
			visibility: visible;
		}

		.gallery-nav-btn {
			pointer-events: auto;
			background: rgba(0, 0, 0, 0.75);
			border: none;
			border-radius: 50%;
			width: 44px;
			height: 44px;
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: #fff;
			transition: all 0.3s ease;
		}

		.gallery-nav-btn:hover {
			background: #F7941D;
			transform: scale(1.1);
		}

		.gallery-nav-btn:active {
			transform: scale(0.95);
		}

		.gallery-nav-btn svg {
			width: 20px;
			height: 20px;
		}

		/* Ensure gallery container has position for absolute elements */
		.project-gallery {
			position: relative;
		}

		/* Mobile adjustments */
		@media (max-width: 768px) {
			.gallery-lightbox-counter {
				top: 0.5rem;
				right: 0.5rem;
				font-size: 0.75rem;
				padding: 0.375rem 0.75rem;
			}

			.gallery-nav-btn {
				width: 36px;
				height: 36px;
			}

			.gallery-nav-btn svg {
				width: 16px;
				height: 16px;
			}
		}
	`;
	document.head.appendChild(style);
}
/**
 * Bootstrap modal backdrop cleanup.
 *
 * Opening a second modal while another one is still fading out (e.g. tapping
 * the navbar search icon twice, or search + terms modal overlap) can leave an
 * orphaned `.modal-backdrop` behind that swallows clicks on the page.
 * Once every modal is closed, remove any leftover backdrop and reset the
 * body state Bootstrap would normally clean up itself.
 */
document.addEventListener('hidden.bs.modal', function () {
	// Another modal is still open — Bootstrap still owns the backdrop/body state.
	if (document.querySelector('.modal.show')) {
		return;
	}

	document.querySelectorAll('.modal-backdrop').forEach(function (backdrop) {
		backdrop.remove();
	});

	document.body.classList.remove('modal-open');
	document.body.style.removeProperty('padding-right');
	document.body.style.removeProperty('overflow');
});
